import type { ReactNode } from 'react';
import {
  ELEMENT_CATEGORIES,
  type OverviewFacts,
  type OverviewNode,
  type OverviewNodeId,
  type OverviewSection,
} from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { Disclosure } from '../ui/Disclosure.js';
import { NODE_ICON, RatioBar, StackBar } from './parts.js';

/**
 * HOW FAR EACH WORKSTREAM HAS GOT — the detail behind the workflow strip's
 * figures: three columns, three denominators, no total.
 *
 * LEVEL 3 (redesign of 2026-10-30): folded under "Progress detail", because the
 * strip above already says each stage's state and its one figure and opens it.
 * Before, these were three more cards with their own state and link — the same
 * facts said a second time at the same weight.
 *
 * Each figure carries its own denominator in words beside the bar, and each bar
 * is drawn only over a denominator that exists: no "0 of 0", and no bar at all
 * for a workflow nobody started (its column says that instead). The three are
 * never averaged — a production can finish its locations and never run a
 * breakdown.
 */
export function Readiness({
  facts,
  nodes,
}: {
  facts: OverviewFacts;
  nodes: Readonly<Record<OverviewNodeId, OverviewNode>>;
}) {
  return (
    <Disclosure summary="Progress detail" meta="Scenes, locations and production elements" testId="overview-readiness">
      <div className="cc-ready">
        <ScenesCard facts={facts} node={nodes.SCENES} />
        <LocationsCard facts={facts} node={nodes.LOCATIONS} />
        <ElementsCard facts={facts} node={nodes.BREAKDOWN} />
      </div>
    </Disclosure>
  );
}

function Card({ node, title, children }: { node: OverviewNode; title: string; children: ReactNode }) {
  return (
    <article className="cc-ready-col" data-testid={`overview-ready-${node.id}`} aria-label={title}>
      <h3 className="cc-ready-title">
        <Icon name={NODE_ICON[node.id]} size={15} />
        {title}
      </h3>
      {children}
    </article>
  );
}

/** What a card says when it has nothing to count: the node's own words. */
function StateLine({ node, testId }: { node: OverviewNode; testId: string }) {
  return (
    <p className="cc-state-line" data-testid={testId}>
      <strong style={{ color: 'var(--ink)' }}>{node.headline}.</strong>
      {node.detail === null ? null : ` ${node.detail}${node.detail.endsWith('.') ? '' : '.'}`}
    </p>
  );
}

function unavailable<T>(section: OverviewSection<T>): boolean {
  return section.status !== 'OK';
}

function ScenesCard({ facts, node }: { facts: OverviewFacts; node: OverviewNode }) {
  const s = facts.scenes.status === 'OK' ? facts.scenes.value : null;
  const hasScenes = s !== null && s.active + s.excluded > 0 && node.state !== 'NOT_STARTED';

  return (
    <Card node={node} title="Scenes">
      {unavailable(facts.scenes) || !hasScenes ? (
        <StateLine node={node} testId="overview-ready-SCENES-state" />
      ) : (
        <>
          <p className="cc-figure" data-testid="overview-ready-SCENES-figure">
            <span className="cc-figure-n">{s.active}</span>
            <span className="cc-figure-l">
              active {s.active === 1 ? 'scene' : 'scenes'}
              {s.detected !== s.active ? ` · ${String(s.detected)} detected by the parser` : ''}
            </span>
          </p>
          {s.active > 0 ? (
            <>
              <StackBar
                label={`${String(s.confirmed)} confirmed, ${String(s.toReview)} to review, ${String(s.needsAttention)} need attention, of ${String(s.active)} active scenes`}
                parts={[
                  { value: s.confirmed, tone: 'ok' },
                  { value: s.toReview, tone: 'muted' },
                  { value: s.needsAttention, tone: 'warn' },
                ]}
              />
              <ul className="cc-legend">
                <li data-testid="overview-ready-SCENES-confirmed">
                  <span className="cc-swatch" data-tone="ok" />
                  <b>{s.confirmed}</b> confirmed
                </li>
                <li data-testid="overview-ready-SCENES-to-review">
                  <span className="cc-swatch" data-tone="muted" />
                  <b>{s.toReview}</b> to review
                </li>
                <li data-testid="overview-ready-SCENES-attention">
                  <span className="cc-swatch" data-tone="warn" />
                  <b>{s.needsAttention}</b> need attention
                </li>
              </ul>
            </>
          ) : null}
          {s.excluded > 0 || s.outOfDate > 0 || s.unread > 0 ? (
            <p className="cc-note" style={{ margin: '10px 0 0' }} data-testid="overview-ready-SCENES-note">
              {[
                s.excluded > 0 ? `${String(s.excluded)} excluded, not counted` : null,
                s.outOfDate > 0 ? `${String(s.outOfDate)} changed since the AI read them` : null,
                s.unread > 0 ? `${String(s.unread)} not read by the AI yet` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function LocationsCard({ facts, node }: { facts: OverviewFacts; node: OverviewNode }) {
  const L = facts.locations.status === 'OK' ? facts.locations.value : null;
  const counted = L !== null && L.total > 0;

  return (
    <Card node={node} title="Locations">
      {!counted ? (
        <StateLine node={node} testId="overview-ready-LOCATIONS-state" />
      ) : (
        <>
          <p className="cc-figure" data-testid="overview-ready-LOCATIONS-figure">
            <span className="cc-figure-n">{L.total}</span>
            <span className="cc-figure-l">
              location {L.total === 1 ? 'requirement' : 'requirements'}
              {L.dismissed > 0 ? ` · ${String(L.dismissed)} not needed` : ''}
            </span>
          </p>
          <p className="cc-note" style={{ margin: '4px 0 0' }}>
            Each is one place, however many scenes use it.
          </p>
          <ul className="cc-funnel">
            <FunnelRow
              id="reviewed"
              label="Reviewed"
              value={L.total - L.undecided}
              of={L.total}
              tone="ok"
            />
            {L.approved === 0 ? (
              <li className="cc-note" data-testid="overview-ready-LOCATIONS-none-approved">
                No requirement approved yet — searching starts from an approved one.
              </li>
            ) : (
              <>
                <FunnelRow id="searched" label="Searched" value={L.searched} of={L.approved} tone="ink" />
                <FunnelRow id="candidates" label="Candidates found" value={L.withCandidates} of={L.approved} tone="ink" />
                <FunnelRow id="shortlisted" label="Shortlisted" value={L.shortlisted} of={L.approved} tone="ink" />
                <FunnelRow id="selected" label="Location confirmed" value={L.selected} of={L.approved} tone="ok" />
              </>
            )}
          </ul>
          {L.unmatched > 0 ? (
            <p className="cc-note" style={{ margin: '10px 0 0', color: 'var(--warn)' }} data-testid="overview-ready-LOCATIONS-unmatched">
              {L.unmatched} searched with no candidate found
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function FunnelRow({
  id,
  label,
  value,
  of,
  tone,
}: {
  id: string;
  label: string;
  value: number;
  of: number;
  tone: 'ok' | 'ink';
}) {
  return (
    <li className="cc-funnel-row" data-testid={`overview-ready-LOCATIONS-${id}`}>
      <span>{label}</span>
      <span className="cc-num">
        {value} of {of}
      </span>
      <RatioBar value={value} of={of} tone={tone} label={`${label}: ${String(value)} of ${String(of)}`} />
    </li>
  );
}

function ElementsCard({ facts, node }: { facts: OverviewFacts; node: OverviewNode }) {
  const B = facts.breakdown.status === 'OK' ? facts.breakdown.value : null;
  const active = facts.scenes.status === 'OK' ? (facts.scenes.value?.active ?? 0) : 0;
  const counted = B !== null && (B.proposals > 0 || B.confirmedTags > 0 || B.scenesSignedOff > 0);
  // How much of the screenplay the AI has read: the strip's stage step, which no
  // figure here repeats.
  const reading = node.steps.find((step) => step.id === 'read');

  return (
    <Card node={node} title="Production elements">
      {!counted ? (
        <StateLine node={node} testId="overview-ready-BREAKDOWN-state" />
      ) : (
        <>
          <p className="cc-figure" data-testid="overview-ready-BREAKDOWN-figure">
            <span className="cc-figure-n">{B.confirmedTags}</span>
            <span className="cc-figure-l">
              confirmed on the sheet · {B.uniqueElements} distinct · {B.toReview} to review
            </span>
          </p>
          {B.categories.length > 0 ? (
            <ul className="cc-matrix" data-testid="overview-ready-BREAKDOWN-matrix" aria-label="Breakdown by category">
              {B.categories.map((c) => {
                const meta = ELEMENT_CATEGORIES[c.category];
                return (
                  <li key={c.category} className="cc-matrix-row" data-testid={`overview-category-${c.category}`}>
                    <span className="cc-matrix-label">
                      <span className="cc-swatch" style={{ background: meta.colour }} aria-hidden="true" />
                      {meta.label}
                    </span>
                    <StackBar
                      label={`${meta.label}: ${String(c.confirmed)} confirmed, ${String(c.toReview)} to review`}
                      parts={[
                        { value: c.confirmed, tone: 'ok' },
                        { value: c.toReview, tone: 'warn' },
                      ]}
                    />
                    <span className="cc-num" title={c.rejected > 0 ? `${String(c.rejected)} rejected` : undefined}>
                      {c.confirmed}
                      {c.toReview > 0 ? <span style={{ color: 'var(--warn)' }}>{` · ${String(c.toReview)} to review`}</span> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <ul className="cc-legend">
            <li>
              <span className="cc-swatch" data-tone="ok" /> confirmed
            </li>
            <li>
              <span className="cc-swatch" data-tone="warn" /> AI proposals to review
            </li>
          </ul>
          {reading === undefined ? null : (
            <p className="cc-note" style={{ margin: '12px 0 0' }} data-testid="overview-ready-BREAKDOWN-reading">
              {reading.label}: <strong style={{ color: 'var(--ink)' }}>{reading.value}</strong>
            </p>
          )}
          {active > 0 ? (
            <div className="cc-funnel" style={{ marginTop: 14 }}>
              <div className="cc-funnel-row" data-testid="overview-ready-BREAKDOWN-signed-off">
                <span>Scenes signed off</span>
                <span className="cc-num">
                  {B.scenesSignedOff} of {active}
                </span>
                <RatioBar
                  value={B.scenesSignedOff}
                  of={active}
                  tone="ok"
                  label={`Scenes signed off: ${String(B.scenesSignedOff)} of ${String(active)}`}
                />
              </div>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
